// Apolo IDE — TeamPlanCard (sprint subagents-team, E3-WS3-T03).
//
// O card do MODO EQUIPE no chat: raciocínio do líder (1-liner expansível,
// idioma do ThinkingBlock) + plano (operários com papel) + chips de estado ao
// vivo + rollup de tokens/custo. UMA fonte de shape pros dois mundos:
// `message.metadata` — ao vivo o runner do team turn atualiza esse objeto via
// updateMsg; persistido ele volta do banco no MESMO formato (role='tool',
// tool_name='team_plan', metadata=tool_payload). Reload-safe por construção.
//
// metadata = { plan: {reasoning, subtasks:[{title, role}]},
//              children: [{title, state}], stats?: {tokens, costUsd, durationMs,
//              synthSource}, parentRunId? }

import { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, ListTree, Users } from 'lucide-react';
import { RunStateChip } from '../../hermes/RunCard.jsx';
import { fmtTokens } from '../../hermes/runFormat.js';
import SquadStatusChip from './SquadStatusChip.jsx';
import SquadCallLogDrawer from './SquadCallLogDrawer.jsx';

function fmtCost(usd) {
  if (typeof usd !== 'number' || !(usd > 0)) return null;
  return usd < 0.01 ? '<$0.01' : `$${usd.toFixed(2)}`;
}

export default function TeamPlanCard({ message }) {
  const [open, setOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const meta = message?.metadata || {};
  const plan = meta.plan || {};
  const subtasks = Array.isArray(plan.subtasks) ? plan.subtasks : [];
  const children = Array.isArray(meta.children) ? meta.children : [];
  const stats = meta.stats || null;
  const cost = stats ? fmtCost(stats.costUsd) : null;

  return (
    <div className="rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface1/50 px-3 py-2 text-[12px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open ? 'true' : 'false'}
        className="flex w-full items-center gap-2 text-left text-apolo-textSecondary transition hover:text-apolo-textPrimary"
      >
        {open ? (
          <ChevronDown size={12} aria-hidden="true" />
        ) : (
          <ChevronRight size={12} aria-hidden="true" />
        )}
        <Users size={12} className="shrink-0 text-apolo-accent" aria-hidden="true" />
        <span className="min-w-0 truncate font-medium">
          Equipe — {subtasks.length} operário{subtasks.length === 1 ? '' : 's'}
        </span>
        {stats?.tokens > 0 ? (
          <span className="ml-auto shrink-0 text-apolo-textTertiary">
            {fmtTokens(stats.tokens)} tokens{cost ? ` · ${cost}` : ''}
          </span>
        ) : null}
      </button>

      {/* Operários: sempre visíveis (é o coração do card), com chip de estado. */}
      <ul className="mt-2 space-y-1" aria-label="Operários da equipe">
        {subtasks.map((s, i) => {
          const child = children[i];
          return (
            <li key={i} className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-mono text-[10px] text-apolo-textTertiary">A{i + 1}</span>
              <span className="min-w-0 truncate text-apolo-textPrimary">{s.title}</span>
              <span className="shrink-0 rounded-full bg-apolo-surface2 px-1.5 py-0.5 text-[10px] text-apolo-textTertiary">
                {s.role}
              </span>
              <span className="ml-auto shrink-0">
                {/* E5 — status 4-valorado quando o operário termina; chip de
                    estado do run enquanto está vivo. */}
                {child?.status ? (
                  <SquadStatusChip status={child.status} />
                ) : (
                  <RunStateChip state={child?.state || 'queued'} mini />
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {open && plan.reasoning ? (
        <p className="mt-2 whitespace-pre-wrap border-t border-apolo-borderSubtle pt-2 text-apolo-textTertiary">
          {plan.reasoning}
        </p>
      ) : null}
      {open && stats?.synthSource === 'fallback' ? (
        <p className="mt-1 text-[11px] text-apolo-warning">
          Síntese determinística (o líder não validou as citações) — resultados brutos preservados.
        </p>
      ) : null}
      {open && meta.parentRunId ? (
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              window.dispatchEvent(new CustomEvent('apolo:openActivity', { detail: { runId: meta.parentRunId } }));
            }}
            className="inline-flex items-center gap-1 text-[11px] text-apolo-textTertiary transition hover:text-apolo-textPrimary"
          >
            <ExternalLink size={11} aria-hidden="true" />
            Ver na atividade
          </button>
          {/* E5 — trilha de auditoria das chamadas do orquestrador */}
          <button
            type="button"
            onClick={() => setLogOpen((v) => !v)}
            aria-expanded={logOpen ? 'true' : 'false'}
            className="inline-flex items-center gap-1 text-[11px] text-apolo-textTertiary transition hover:text-apolo-textPrimary"
          >
            <ListTree size={11} aria-hidden="true" />
            Ver chamadas
          </button>
        </div>
      ) : null}
      {logOpen && meta.parentRunId ? (
        <SquadCallLogDrawer runId={meta.parentRunId} onClose={() => setLogOpen(false)} />
      ) : null}
    </div>
  );
}
